import type { ReactNode } from 'react';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Link from '@mui/material/Link';
import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import ReportProblemOutlinedIcon from '@mui/icons-material/ReportProblemOutlined';
import { useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import {
  civilToday,
  claimStatusTone,
  formatCivilDate,
  links,
  resolveFormattingLocale,
} from '@eis/core';
import { DetailsSection, EmptyState, EntityHeader, ErrorState, PageLayout } from '@eis/patterns';
import { getRuntimeConfig, isNotFound, useClaim, useCoverageAtDate } from '../api/claims';
import {
  categoryLabel,
  civilOf,
  daysOpen,
  amountText,
  isGroupAccount,
  objectNameOf,
} from '../model/claims';
import { isClaimServiceDown } from './ClaimListPage';
import { CoveragePanel } from './CoveragePanel';

/**
 * One claim (SDD-018 §9, ui-spec §7): v1's claim detail, read-only. Handling — assigning,
 * positions, regulations, deleting — stays in the suite (C-12): the one action is "Im
 * Schadenmodul öffnen", the claim under the configured `suiteUrl`.
 *
 * The coverage card is new: `GetCoverageAtDate` at the claim's damage date — the variant, the
 * claim type's term, the deductible and the waiting period; for a member, the group's terms and
 * "Deckung unter Gruppenvertrag {code}" with the group's policyholder as the liable party.
 */
export function ClaimDetailPage({ code }: { readonly code: string }): ReactNode {
  const { t, i18n } = useTranslation('claims');
  const locale = resolveFormattingLocale(i18n.language);
  const navigate = useNavigate();
  const query = useClaim(code);
  const claim = query.data?.claim;
  const damageDate = civilOf(claim?.damageDate);
  const coverage = useCoverageAtDate({
    policyCode: claim?.policyCode ?? '',
    damageDate,
  });

  if (query.isPending) {
    return (
      <PageLayout header={<Skeleton variant="text" width={320} height={44} />}>
        <Skeleton variant="rounded" height={260} />
      </PageLayout>
    );
  }
  if (query.isError) {
    return (
      <PageLayout>
        <ErrorState
          description={
            isNotFound(query.error)
              ? t('detail.error.notFound', { code })
              : isClaimServiceDown(query.error)
                ? t('detail.error.unavailable')
                : t('detail.error.detail')
          }
          details={query.error.message}
          onRetry={() => {
            void query.refetch();
          }}
        />
      </PageLayout>
    );
  }
  if (claim === undefined) {
    return (
      <PageLayout>
        <EmptyState description={t('detail.error.notFound', { code })} />
      </PageLayout>
    );
  }

  const date = (value: string | undefined): string => {
    const civil = civilOf(value);
    return civil === '' ? '—' : formatCivilDate(civil, locale);
  };
  const number = claim.claimNumber === '' ? claim.code : claim.claimNumber;
  const suiteHref = links.claims.suite(getRuntimeConfig().chrome.suiteUrl, claim.code);
  const answer = coverage.data;
  const groupCode = answer?.groupPolicy?.code ?? (isGroupAccount(claim) ? claim.accountCode : '');
  const category = (answer?.coverage?.categories ?? []).find((c) => c.key === claim.claimType);
  const object = (answer?.insuredObjects ?? []).find(
    (o) => o.name === objectNameOf(claim.policyObjectCode),
  );
  const partners = claim.claimPartners ?? [];
  const billing = answer?.billing;

  const internalLink = (href: string, text: string): ReactNode => (
    <Link
      href={href}
      sx={{ fontFamily: 'monospace' }}
      onClick={(event) => {
        event.preventDefault();
        void navigate({ href });
      }}
    >
      {text}
    </Link>
  );

  return (
    <PageLayout
      header={
        <EntityHeader
          title={t('detail.title', { number })}
          icon={<ReportProblemOutlinedIcon />}
          status={{
            value: claim.status,
            label: t(`status.${claim.status}` as never, { defaultValue: claim.status }),
            tone: claimStatusTone(claim.status),
          }}
          backTo={{
            label: t('detail.back'),
            href: links.claims.claims(),
            onNavigate: (event) => {
              event.preventDefault();
              void navigate({ href: links.claims.claims() });
            },
          }}
          meta={[
            { label: t('detail.openedAt'), value: date(claim.createdAt) },
            { label: t('detail.updatedAt'), value: date(claim.updatedAt) },
            { label: t('detail.updatedBy'), value: claim.updatedBy === '' ? '—' : claim.updatedBy },
          ]}
          actions={
            <Button
              variant="contained"
              href={suiteHref}
              target="_blank"
              rel="noopener noreferrer"
              endIcon={<OpenInNewIcon />}
            >
              {t('detail.openInSuite')}
            </Button>
          }
        />
      }
    >
      <Stack spacing={2.5}>
        <DetailsSection
          title={t('detail.contract.title')}
          fields={[
            {
              label: t('detail.contract.policy'),
              value: internalLink(links.insurance.policy(claim.policyCode), claim.policyCode),
            },
            {
              label: t('detail.contract.product'),
              value: claim.productName === '' ? '—' : claim.productName,
            },
            {
              label: t('detail.contract.daysOpen'),
              value: <Chip size="small" label={daysOpen(claim, civilToday())} />,
            },
            ...(groupCode === ''
              ? []
              : [
                  {
                    label: t('detail.contract.groupPolicy'),
                    value: (
                      <span data-testid="claim-group-cover">
                        {t('detail.contract.groupCover', { code: groupCode })}{' '}
                        {internalLink(links.insurance.groupPolicy(groupCode), groupCode)}
                      </span>
                    ),
                  },
                ]),
            ...(answer?.liableParty === undefined
              ? []
              : [
                  {
                    label: t('detail.contract.liableParty'),
                    value:
                      answer.liableParty.displayName === undefined ||
                      answer.liableParty.displayName === ''
                        ? (answer.liableParty.partnerId ?? '—')
                        : answer.liableParty.displayName,
                  },
                ]),
          ]}
        />

        <DetailsSection
          title={t('detail.adjuster.title')}
          fields={[
            {
              label: t('detail.adjuster.label'),
              value:
                (claim.adjuster ?? '') === ''
                  ? t('detail.adjuster.unassigned')
                  : (claim.adjuster ?? ''),
            },
          ]}
        />

        <DetailsSection
          title={t('detail.premium.title')}
          fields={[
            {
              label: t('detail.premium.invoices'),
              value:
                billing === undefined
                  ? t('detail.premium.none')
                  : t('detail.premium.summary', {
                      count: billing.openInvoices ?? 0,
                      status:
                        billing.lastInvoiceStatus === undefined || billing.lastInvoiceStatus === ''
                          ? '—'
                          : billing.lastInvoiceStatus,
                    }),
            },
          ]}
        />

        <Stack spacing={1} component="section" aria-labelledby="claim-parties">
          <Typography variant="h3" component="h2" id="claim-parties">
            {t('detail.parties.title')}
          </Typography>
          {partners.length === 0 ? (
            <EmptyState description={t('detail.parties.empty')} />
          ) : (
            <Table size="small" aria-labelledby="claim-parties">
              <TableHead>
                <TableRow>
                  <TableCell>{t('detail.parties.partner')}</TableCell>
                  <TableCell>{t('detail.parties.role')}</TableCell>
                  <TableCell>{t('detail.parties.tags')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {partners.map((partner) => (
                  <TableRow
                    key={`${partner.partnerCode}-${partner.claimPartnerRoleCode}`}
                    data-testid="claim-party"
                  >
                    <TableCell>
                      {internalLink(
                        links.partners.partner(partner.partnerCode),
                        partner.partnerCode,
                      )}
                    </TableCell>
                    <TableCell>
                      {partner.claimPartnerRole?.name ?? partner.claimPartnerRoleCode}
                    </TableCell>
                    <TableCell>—</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </Stack>

        <DetailsSection
          title={t('detail.details.title')}
          fields={[
            {
              label: t('detail.details.product'),
              value: claim.productName === '' ? '—' : claim.productName,
            },
            {
              label: t('detail.details.objects'),
              value:
                claim.policyObjectCode === ''
                  ? '—'
                  : object?.label === undefined || object.label === ''
                    ? claim.policyObjectCode
                    : `${object.label} (${claim.policyObjectCode})`,
            },
            { label: t('detail.details.notificationDate'), value: date(claim.notificationDate) },
            { label: t('detail.details.damageDate'), value: date(claim.damageDate) },
            { label: t('detail.details.damageEndDate'), value: date(claim.damageEndDate) },
            {
              label: t('detail.details.claimType'),
              value:
                category === undefined
                  ? claim.claimType === ''
                    ? '—'
                    : claim.claimType
                  : categoryLabel(category),
            },
            {
              label: t('detail.details.claimTitle'),
              value: claim.title === '' ? '—' : claim.title,
            },
            {
              label: t('detail.details.description'),
              value: claim.description === '' ? '—' : claim.description,
              wide: true,
            },
            {
              label: t('detail.details.initialCost'),
              value: amountText(claim.initialCost, locale, t('list.notSubmitted')),
            },
            {
              label: t('detail.details.reimbursement'),
              value: amountText(claim.reimbursementAmount, locale, t('list.noReimbursement')),
            },
          ]}
        />

        {coverage.isError ? (
          <ErrorState
            description={t('coverage.loadError')}
            details={coverage.error.message}
            onRetry={() => void coverage.refetch()}
          />
        ) : answer === undefined ? (
          <Skeleton variant="rounded" height={160} data-testid="coverage-loading" />
        ) : (
          <CoveragePanel
            coverage={answer}
            damageDate={damageDate}
            locale={locale}
            claimType={claim.claimType}
          />
        )}
      </Stack>
    </PageLayout>
  );
}
